/**
 * Iter-411 — Bulk grade uploader (CSV / XLSX).
 *
 * Small dialog reusable from the Publishing tab or Submissions tab. Posts to
 *   POST /api/school/portal/teacher/marks/import
 * (form-data: exam_id, subject_id, file). Returns imported/skipped/errors
 * which we surface inline so the marker can see exactly which rows failed
 * and why.
 */
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Upload, CheckCircle2, AlertCircle, FileUp } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const _token = () => {
  try { const p = sessionStorage.getItem('school_preview_token'); if (p) return p; }
  catch { /* ignore */ }
  return getToken();
};

const BulkGradeDialog = ({ open, onOpenChange, examId, subjects, onDone }) => {
  const [subjectId, setSubjectId] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  React.useEffect(() => {
    if (open) {
      setSubjectId(subjects?.[0]?.subject_id || subjects?.[0]?.id || '');
      setFile(null); setResult(null); setBusy(false);
    }
  }, [open, subjects]);

  const run = async () => {
    if (!file || !subjectId) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('exam_id', examId);
      fd.append('subject_id', subjectId);
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/school/portal/teacher/marks/import`,
        { method: 'POST', headers: { Authorization: `Bearer ${_token()}` }, body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || 'Import failed');
      const r = await res.json();
      setResult(r);
      toast.success(`${r.imported} marks imported · ${r.skipped} skipped`);
      onDone?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-grade-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="h-4 w-4 text-indigo-500" /> Bulk mark entry
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[11px] text-slate-500">
            Upload CSV or XLSX. Required columns: <code>admission_number</code> (or <code>student_id</code>)
            and <code>marks</code>. Optional: <code>remarks</code>.
            <br />
            Marks default to <b>unpublished</b> — use the <b>Publishing</b> tab to release them.
          </p>
          <div>
            <Label className="text-xs">Subject</Label>
            <Select value={subjectId} onValueChange={setSubjectId}>
              <SelectTrigger data-testid="bulk-subject-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(subjects || []).map(s => (
                  <SelectItem key={s.subject_id || s.id} value={s.subject_id || s.id}>
                    {s.subject_name || s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">File</Label>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                     onChange={(e) => setFile(e.target.files?.[0] || null)}
                     data-testid="bulk-file-input" />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}
                      data-testid="bulk-pick-file">
                <Upload className="h-4 w-4 mr-1" /> Pick file
              </Button>
              <span className="text-xs text-slate-500 truncate">{file?.name || 'No file'}</span>
            </div>
          </div>
          {result && (
            <div className="text-xs bg-slate-50 border rounded p-2 space-y-1">
              <div>
                <CheckCircle2 className="h-3 w-3 inline text-emerald-600 mr-1" />
                {result.imported} imported
                {result.skipped > 0 && <> · <AlertCircle className="h-3 w-3 inline text-amber-600 mx-1" />{result.skipped} skipped</>}
              </div>
              {result.errors?.length > 0 && (
                <ul className="mt-1 text-rose-600 max-h-32 overflow-auto">
                  {result.errors.map((e, i) => <li key={i}>· {e}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Close</Button>
          <Button onClick={run} disabled={!file || !subjectId || busy} data-testid="bulk-run">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
            Import marks
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkGradeDialog;
